/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/Buttons/Button';
import Pagination from 'components/Pagination';
import ButtonDropdown from 'components/Buttons/ButtonDropdown';
import InputDate from 'components/Inputs/inputDate';
import InputText from 'components/Inputs/inputText';
import InputNumber from 'components/Inputs/inputNumber';
import InputSelect from 'components/Inputs/inputSelect';
import InputMultiSelect from 'components/Inputs/inputMultiSelect';
import EmptyStreet from 'assets/svg/empty_street.svg';
import Searcher from 'components/Searcher';
import { literalTemplate } from 'utils/language';
import { dictionary, formatErrors, formatNumber } from 'utils/functions';
import { Link } from 'react-router-dom';
import {
  Eraser, Filter, ToggleRight, ToggleLeft,
} from 'lucide-react';
import PermissionPopup from 'components/PermissionsPopup/PermissionPopup';
import { useWidth } from 'utils/customHooks';
import { Alert, Table as TableUI } from 'xland-ui';
import PopupConfigTableColumns from './PopupConfigTableColumns';
import { getTable } from '../modules/actions';
import './styles.scss';
import TableComponents from '../TableComponents';

function Table(props) {
  const {
    className,
    literals,
    searcher,
    filters,
    actions,
    actionsButtons = 1,
    rowSelection,
    columns: auxColumns,
    component,
    componentProps,
    componentColumns,
    emptyImage,
    emptyMessage,
    fetch,
    onFetch,
    pageSize,
    forceFetch,
    forceFetchAuto,
    fetchLocal,
    onClickRow,
    selectRows,
    onSelectRows,
    onDrag,
    externalFilterValue,
    configColumns,
    rowDetails,
    alert,
  } = props;

  const defaultValues = () => filters.reduce((prev, curr) => {
    const aux = { ...prev };
    if (curr.default) {
      aux[curr.id] = curr.default;
    }
    return aux;
  }, {});

  const originalColumns = auxColumns.map((col, i) => ({ ...col, order: i, hide: false }));
  const [rows, setRows] = useState(null);
  const [savedColumns, setSavedColumns] = useState({ columns: originalColumns, edited: false, loaded: false });
  const [search, setSearch] = useState('');
  const [filtersValue, setFiltersValue] = useState(defaultValues());
  const [loading, setLoading] = useState({ fetch: true, column: false });
  const [actualPage, setActualPage] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState(selectRows);
  const [sortedColumn, setSortedColumn] = useState({ field: '', order: '' });
  // const [loadingOrder, setLoadingOrder] = useState(false);
  // const [allowedDrag, setAllowedDrag] = useState({ onDrag: !!onDrag });
  const [showConfigColumns, setShowConfigColumns] = useState(false);
  const [showPermissionPopup, setShowPermissionPopup] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const columns = configColumns ? savedColumns : { columns: originalColumns };
  // const scrollRef = useRef();
  const width = useWidth();

  const isLoading = Object.values(loading).some(l => l);

  useEffect(() => {
    if (!savedColumns.edited) {
      setSavedColumns({ columns: originalColumns, edited: false, loaded: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auxColumns]);

  useEffect(() => {
    if (fetch && configColumns && !savedColumns.loaded) {
      (async () => {
        setLoading(prev => ({ ...prev, column: true }));
        const newSavedColumns = await getTable(configColumns, originalColumns);
        if (newSavedColumns) {
          const auxCols = dictionary(originalColumns.map(col => ({ ...col, id: col.id || col.field })));
          setSavedColumns({
            columns: newSavedColumns.filter(col => [auxCols[col.id]?.id, auxCols[col.id]?.field].includes(col.id)),
            edited: true,
            loaded: true,
          });
        } else {
          setSavedColumns({ columns: originalColumns, edited: false, loaded: true });
        }
        setLoading(prev => ({ ...prev, column: false }));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetch, configColumns]);

  useEffect(() => {
    if (onSelectRows) {
      onSelectRows(selectedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  useEffect(() => {
    setSelectedRows(selectRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectRows]);

  // const getRowSelectFields = (row) => {
  //   const auxRow = {};
  //   selectFields.forEach((att) => {
  //     const splitedAtt = typeof att === 'object' ? att.field.split('.') : att.split('.');
  //     const val = splitedAtt.reduce((prev, curr) => prev[curr], row);
  //     const key = typeof att === 'object' ? att?.name || att[att.length - 1] : att;
  //     auxRow[key] = val;
  //   });
  //   return auxRow;
  // };

  // const handleSelectRow = (row) => {
  //   setSelectedRows((prev) => {
  //     const newSelectedRows = [...prev];
  //     if (selectFields) {
  //       if (!selectedRows.find(auxRow => auxRow.id === row.id)) {
  //         newSelectedRows.push(getRowSelectFields(row));
  //       }
  //     } else if (!selectedRows.includes(row.id)) {
  //       newSelectedRows.push(row.id);
  //     }
  //     return newSelectedRows;
  //   });
  // };

  // const handleUnselectRow = (row) => {
  //   if (selectFields) {
  //     setSelectedRows(prev => prev.filter(auxRow => auxRow.id !== row.id));
  //   } else {
  //     setSelectedRows(prev => prev.filter(id => id !== row.id));
  //   }
  // };

  // const selectAllRows = async () => {
  //   setLoading(prev => ({ ...prev, fetch: true }));
  //   const { items } = await fetch(0, 0, search, filtersValue, '', selectFields ? selectFields.join(',') : 'id');
  //   setSelectedRows(items);
  //   setLoading(prev => ({ ...prev, fetch: false }));
  // };

  // const selectPageRows = () => {
  //   rows.items.forEach((item) => {
  //     handleSelectRow(item);
  //   });
  //   setLoading(prev => ({ ...prev, fetch: false }));
  // };

  const fetchPage = async (page = 0, auxFilters = null) => {
    if (!fetchLocal) {
      setLoading(prev => ({ ...prev, fetch: true }));
    }
    if (fetch) {
      if (fetchError) {
        setFetchError(false);
      }
      try {
        const SORT = {
          asc: '+',
          desc: '-',
        };
        const response = await fetch(page, pageSize, search, auxFilters || filtersValue, sortedColumn.order ? `${SORT[sortedColumn.order] || ''}${sortedColumn.field}` : '');
        setRows(response);
        if (configColumns) {
          setSavedColumns(prev => ({
            ...prev,
            columns: prev.columns.map(col => ({
              ...col,
              hide: col.hide || (response && typeof col.hidden === 'function' && col.hidden(response.items)),
            })),
          }));
        }
        setLoading(prev => ({ ...prev, fetch: false }));
        if (typeof onFetch === 'function') {
          onFetch(response);
        }
      } catch (error) {
        const auxError = (error?.response && error.statusText) ? formatErrors(error) : error;
        setFetchError(auxError?.unauthorized ? literals.errorFetchingUnauthorized : literals.errorFetchingGeneric);
        setRows({ items: [], total: 0 });
        setLoading(prev => ({ ...prev, fetch: false }));
      }
    }
    // setAllowedDrag(prev => ({
    //   ...prev,
    //   search: !search,
    //   filters: !Object.keys(filtersValue).length,
    // }));
  };

  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedColumn]);

  useEffect(() => {
    (async () => {
      if (externalFilterValue) {
        setFiltersValue(externalFilterValue);
        fetchPage(0, externalFilterValue);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalFilterValue]);

  const onChangeFilterValue = (id, value, key = null) => {
    const auxfiltersValue = { ...filtersValue };

    if (Array.isArray(id)) {
      id.forEach((singleId) => {
        onChangeFilterValue(singleId, value, key);
      });
    } else if (key) {
      if (!value && value !== 0) {
        if (auxfiltersValue[id]) {
          delete auxfiltersValue[id][key];
          if (!Object.keys(auxfiltersValue[id]).length) {
            delete auxfiltersValue[id];
          }
        }
      } else {
        auxfiltersValue[id] = { ...(auxfiltersValue[id] || {}), [key]: value };
      }
    } else if ((!value && value !== 0) || (Array.isArray(value) && !value.length)) {
      delete auxfiltersValue[id];
    } else {
      auxfiltersValue[id] = value;
    }
    setFiltersValue(auxfiltersValue);
  };

  const cleanFilters = () => {
    setFiltersValue(defaultValues());
  };

  const applyFilters = () => {
    fetchPage(0);
    if (actualPage !== 0) {
      setActualPage(0);
    }
    setShowFilters(false);
  };

  const handleValidateRange = async (filter, val, key) => {
    const auxfiltersValue = { ...filtersValue };
    if (val) {
      const other = key === 'gte' ? 'lte' : 'gte';
      if ((key === 'gte' && val > auxfiltersValue[filter][other])
        || (key === 'lte' && val < auxfiltersValue[filter][other])) {
        auxfiltersValue[filter][key] = auxfiltersValue[filter][other];

        setFiltersValue(auxfiltersValue);
      }
    }
  };

  // const handleOpenPermissions = (row, cfg) => {
  //   setShowPermissionPopup({
  //     title: cfg.title,
  //     element: { ...cfg.element, id: row.id },
  //     scope: cfg.scope,
  //   });
  // };

  const handleClosePermissionPopup = (edited) => {
    setShowPermissionPopup(false);
    if (edited) {
      fetchPage(actualPage);
    }
  };

  const renderActions = () => {
    const notHiddenActions = actions.filter(action => !action.hidden);
    const visibleActions = notHiddenActions.slice(0, actionsButtons);
    const dropdownActions = notHiddenActions.slice(actionsButtons);

    return (
      <>
        {visibleActions.map((action) => (
          action.type === 'Link' ? (
            <Link key={action.to} to={action.to}>
              <Button {...action} />
            </Link>
          ) : (
            <Button key={action.text || action.name} {...action} />
          )
        ))}
        {dropdownActions.length > 0 && (
          <ButtonDropdown text={literals.actions} buttons={dropdownActions} />
        )}
      </>
    );
  };

  const renderRowSelection = () => (<ButtonDropdown text={`${literals.rowsAction} (${selectedRows.length})`} buttons={rowSelection} color='secondary' />);

  const renderFilters = () => {
    if (!searcher && (!filters || !filters.length)) {
      return null;
    }
    const printFilter = (filter) => {
      switch (filter.type) {
        case 'text':
          return (
            <InputText
              preText={filter.title}
              placeholder={`${literals.search}...`}
              value={filtersValue[filter.id]?.search}
              onChange={v => onChangeFilterValue(filter.id, v, 'search')}
            />
          );
        case 'selectUnique':
          return (
            <InputSelect
              preText={filter.title}
              placeholder={filter.placeholder}
              value={filtersValue[filter.id]}
              options={filter.options}
              onChange={v => onChangeFilterValue(filter.id, v)}
              loading={filter.loading}
            />
          );
        case 'list':
        case 'select':
        case 'radiobutton':
        case 'checkbox':
          return (
            <InputMultiSelect
              preText={filter.title}
              value={filtersValue[filter.id]}
              options={filter.options}
              mode={filter?.mode}
              onChange={v => onChangeFilterValue(filter.id, v)}
            />
          );
        case 'number':
          return (
            <InputNumber
              preText={filter.title}
              value={filtersValue[filter.id]}
              onChange={v => onChangeFilterValue(filter.id, v)}
            />
          );
        case 'numberRange':
          return (
            <div className='d-flex'>
              <InputNumber
                preText={filter.title}
                value={filtersValue[filter.id]?.gte}
                onChange={v => onChangeFilterValue(filter.id, v, 'gte')}
                onBlur={v => handleValidateRange(filter.id, v, 'gte')}
              />
              <InputNumber
                preText='&nbsp;'
                className='ps-2'
                value={filtersValue[filter.id]?.lte}
                onChange={v => onChangeFilterValue(filter.id, v, 'lte')}
                onBlur={v => handleValidateRange(filter.id, v, 'lte')}
              />
            </div>
          );
        case 'date':
          return (
            <InputDate
              preText={filter.title}
              value={filtersValue[filter.id]}
              onChange={v => onChangeFilterValue(filter.id, v)}
            />
          );
        case 'dateRange':
          return (
            <div className='filters-range'>
              <InputDate
                className='range'
                preText={filter.title}
                value={filtersValue[filter.id]?.gte}
                onChange={v => onChangeFilterValue(filter.id, v, 'gte')}
                max={filtersValue[filter.id]?.lte}
                onBlur={v => handleValidateRange(filter.id, v, 'gte')}
              />
              <InputDate
                className='range'
                preText='&nbsp;'
                value={filtersValue[filter.id]?.lte}
                onChange={v => onChangeFilterValue(filter.id, v, 'lte')}
                min={filtersValue[filter.id]?.gte}
                onBlur={v => handleValidateRange(filter.id, v, 'lte')}
              />
            </div>
          );
        default:
          return '';
      }
    };

    const numFilters = Object.keys(filtersValue).length;
    return (
      <div className='table-top'>
        <div className='table-bar'>
          <div className='table-filters-basic'>
            {
              searcher ? (
                <Searcher
                  className='table-searcher'
                  value={search}
                  onChange={setSearch}
                  onSearch={applyFilters}
                  haveFilters={numFilters > 0}
                  onFilters={filters?.length ? () => setShowFilters(!showFilters) : null}
                />
              ) : (
                <div className='input_text mb-0'>
                  <div className='buttons mb-0'>
                    {filters && filters.length > 0 && (
                      <Button
                        icon={Filter}
                        text={`${numFilters > 0 ? `${numFilters} ` : ''}${literals.filters}`}
                        color='primary'
                        className='me-1 dropdown-toggle'
                        style={{ minWidth: 'auto' }}
                        onClick={() => setShowFilters(!showFilters)}
                      />
                    )}
                  </div>
                </div>
              )
            }
          </div>
          {
            ((selectedRows.length && rowSelection?.length) || actions?.length) ? (
              <div className='actions-container'>
                {
                  (selectedRows.length && rowSelection && rowSelection.length) ? (
                    <div className='d-flex gap-2'>
                      {renderRowSelection()}
                    </div>
                  ) : null
                }
                {
                  (actions && actions.length) ? (
                    <div className='d-flex gap-2'>
                      {renderActions()}
                    </div>
                  ) : null
                }
              </div>
            ) : null
          }
        </div>
        {filters && filters.length > 0 && (
          <div className={`table-filters-advanced box ${!showFilters ? 'hidden' : ''}`}>
            <div className='row'>
              {
                filters.map((item, i) => {
                  const htmlFilter = printFilter(item);
                  if (!htmlFilter) {
                    return null;
                  }
                  return (
                    <div
                      key={i}
                      className={`col-sm-${item?.width?.sm || '12'} col-md-${item?.width?.md || '6'} col-lg-${item?.width?.lg || '4'}`}
                    >
                      {htmlFilter}
                    </div>
                  );
                })
              }
            </div>
            <div className='d-flex justify-content-end'>
              <Button
                icon={Eraser}
                text={literals.cleanFilters}
                color='secondary'
                onClick={cleanFilters}
                disabled={!numFilters}
              />
              <Button icon={Filter} text={literals.applyFilters} onClick={applyFilters} />
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleChangeActualPage = (page) => {
    setActualPage(page);
    fetchPage(page);
  };

  useEffect(() => {
    if (forceFetch) {
      fetchPage(actualPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceFetch]);

  useEffect(() => {
    if (forceFetchAuto) {
      fetchPage(actualPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetch, forceFetchAuto]);

  return (
    <div className='table-component'>
      {renderFilters()}
      {configColumns && (
        <div className='config-columns' onClick={() => setShowConfigColumns(true)}>
          {columns.edited
            ? <ToggleRight size={18} className='primary' />
            : <ToggleLeft size={18} />}
          <span>{columns.edited ? literals.edited : literals.columns}</span>
        </div>
      )}
      {alert && !isLoading && (
        <Alert type={alert.type} text={alert.text} className={alert.className} />
      )}
      {component ? (
        <TableComponents
          literals={literals}
          rows={rows}
          component={component}
          componentColumns={componentColumns}
          componentProps={componentProps}
          actualPage={actualPage}
          fetchError={fetchError}
          emptyImage={emptyImage}
          emptyMessage={emptyMessage}
          isLoading={isLoading}
          width={width}
          pageSize={pageSize}
        />
      ) : (
        <TableUI
          className={className}
          columns={auxColumns}
          data={rows?.items || []}
          loading={isLoading}
          onRowClick={onClickRow}
          onDrag={onDrag}
          sort={sortedColumn}
          onSort={(field, order) => setSortedColumn({ field, order })}
          pageSize={pageSize}
          rowDetails={rowDetails}
          EmptyComponent={(
            <div className='panel-empty-message'>
              <img src={emptyImage} alt='empty' />
              {emptyMessage || literals.noResults}
            </div>
          )}
        />
      )}

      <div className='table-pagination'>
        {
          rows && rows.total > 0 && (

            <span // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: literalTemplate(`<span>${literals.showingTableResults}</span>`, {
                  FIRST: `<span class='highlight'>${formatNumber((actualPage * pageSize) + 1)}</span>`,
                  LAST: `<span class='highlight'>${formatNumber(Math.min((actualPage + 1) * pageSize, rows.total))}</span>`,
                  TOTAL: `<span class='highlight'>${formatNumber(rows.total)}</span>`,
                }),
              }}
              className='rows-number'
            />
          )
        }
        {rows !== null && rows.total > rows.items?.length && (
          <div className='pagination-select'>
            <Pagination
              pages={Math.ceil(rows.total / pageSize)}
              actualPage={actualPage}
              onChange={handleChangeActualPage}
            />
            <InputSelect
              options={[...Array(Math.ceil(rows.total / pageSize)).keys()].map((page) => ({
                name: page + 1,
                id: page,
              }))}
              value={actualPage}
              onChange={handleChangeActualPage}
            />
          </div>
        )}
      </div>
      {
        showConfigColumns && (
          <PopupConfigTableColumns
            literals={literals}
            originalColumns={originalColumns}
            columns={columns}
            setColumns={setSavedColumns}
            tableName={configColumns}
            onClose={() => setShowConfigColumns(false)}
          />
        )
      }
      {showPermissionPopup && (
        <PermissionPopup
          title={showPermissionPopup.title}
          scope={showPermissionPopup.scope}
          element={showPermissionPopup.element}
          onClose={handleClosePermissionPopup}
        />
      )}
    </div>
  );
}

Table.propTypes = {
  literals: PropTypes.object.isRequired,
  literalsCommon: PropTypes.object.isRequired,
  searcher: PropTypes.bool,
  className: PropTypes.string,
  filters: PropTypes.array,
  actions: PropTypes.array,
  actionsButtons: PropTypes.number,
  rowSelection: PropTypes.array,
  mode: PropTypes.string,
  columns: PropTypes.array,
  component: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  componentProps: PropTypes.object,
  componentColumns: PropTypes.object,
  fetch: PropTypes.func,
  pageSize: PropTypes.number,
  onFetch: PropTypes.func,
  forceFetch: PropTypes.any,
  forceFetchAuto: PropTypes.bool,
  fetchLocal: PropTypes.bool,
  onClickRow: PropTypes.func,
  emptyImage: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.bool]),
  emptyMessage: PropTypes.string,
  selectRows: PropTypes.array,
  onSelectRows: PropTypes.func,
  configColumns: PropTypes.string,
  rowDetails: PropTypes.func,
  selectFields: PropTypes.array,
  onDrag: PropTypes.func,
  externalFilterValue: PropTypes.object,
  alert: PropTypes.object,
};

Table.defaultProps = {
  className: '',
  mode: 'rows',
  searcher: true,
  filters: [],
  actions: [],
  rowSelection: [],
  columns: [],
  component: null,
  componentProps: null,
  componentColumns: null,
  pageSize: 10,
  forceFetch: 0,
  forceFetchAuto: false,
  fetchLocal: false,
  fetch: null,
  onFetch: null,
  onClickRow: null,
  emptyImage: EmptyStreet,
  emptyMessage: null,
  selectRows: [],
  onSelectRows: null,
  rowDetails: null,
  configColumns: '',
  selectFields: null,
  onDrag: null,
  externalFilterValue: null,
};

export default Table;
